import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  getCompareDashboardScenario,
  getSelectedDashboardScenario,
  getTimeFilter,
} from 'redux/DashboardSliceV2';
import {
  formatMonth,
  getCurrentMonthYyyyMm,
  resolveReportingPeriod,
} from 'utils/reportingPeriodUtils';

import type { ReportingWindow } from 'utils/reportingPeriodUtils';
import type { SnapshotCreateBody } from './types';

/**
 * Build the period/scenario payload for pack snapshots and data_values,
 * aligned with `fetchAllDataValues` in DashboardSliceV2.
 */
function usePackRenderContext(scenarioIdOverride?: number | null): SnapshotCreateBody {
  const timeFilter = useSelector(getTimeFilter);
  const selectedScenario = useSelector(getSelectedDashboardScenario);
  const compareScenario = useSelector(getCompareDashboardScenario);
  const anchorPeriod = useSelector(
    (state: any) => state.dashboardSliceV2?.reporting_anchor_period || getCurrentMonthYyyyMm(),
  );
  const reportingWindow: ReportingWindow = useSelector(
    (state: any) => state.dashboardSliceV2?.reporting_window || 'SINGLE',
  );
  const modelTimeProperties = useSelector(
    (state: any) => state.modelsSlice?.blocks?.time_properties,
  );
  const reportingPreset = useSelector(
    (state: any) => state.dashboardSliceV2?.dashboardReportingPeriod?.preset,
  );

  return useMemo(() => {
    const fyStartMonth = (modelTimeProperties?.fy_start_month as string) || 'Jan';

    const fallbackStart =
      modelTimeProperties?.starting_period != null
        ? formatMonth(new Date(modelTimeProperties.starting_period))
        : '';
    const fallbackEnd = (() => {
      if (!modelTimeProperties?.starting_period) return '';
      const granularity = (modelTimeProperties?.time_granularity || 'M').toString().toUpperCase();
      const periods = Number(modelTimeProperties?.plan_duration_periods);
      const duration = Number.isNaN(periods) ? (granularity === 'M' ? 36 : 3) : periods;
      const monthsToAdd = granularity === 'M' ? duration : duration * 12;
      const end = new Date(modelTimeProperties.starting_period);
      end.setMonth(end.getMonth() + monthsToAdd);
      return formatMonth(end);
    })();

    const startDate = timeFilter?.start || fallbackStart;
    const endDate = timeFilter?.end || fallbackEnd;

    const scenarioId = scenarioIdOverride ?? selectedScenario?.id ?? null;
    const compareScenarioId = compareScenario?.id ?? null;

    const resolvedReporting =
      reportingWindow === 'DISPLAY_TIMESCALE'
        ? { start: startDate, end: endDate }
        : resolveReportingPeriod(anchorPeriod, reportingWindow, fyStartMonth);

    return {
      scenario_id: scenarioId,
      scenario_name: selectedScenario?.name ?? null,
      compare_scenario: compareScenarioId,
      compare_scenario_name: compareScenario?.name ?? null,
      start_date: startDate || null,
      end_date: endDate || null,
      reporting_anchor_period: anchorPeriod,
      reporting_window: reportingWindow,
      reporting_start_date: resolvedReporting.start || null,
      reporting_end_date: resolvedReporting.end || null,
      preset: reportingPreset || null,
      fy_start_month: fyStartMonth || null,
    };
  }, [
    anchorPeriod,
    compareScenario?.id,
    compareScenario?.name,
    modelTimeProperties,
    reportingPreset,
    reportingWindow,
    scenarioIdOverride,
    selectedScenario?.id,
    selectedScenario?.name,
    timeFilter?.end,
    timeFilter?.start,
  ]);
}

export default usePackRenderContext;
